import { useApiMutation, useApiQuery, type ApiClient, type ApiError } from "@gladis/api-client";
import type { UseQueryResult } from "@tanstack/react-query";

import type { InvestmentsPageDto, StockDetailDto } from "./dto.js";

/**
 * The page's query, the detail view's query, and every mutation that invalidates them.
 *
 * NO `refetchOnMount`. That exemption belongs to the Expenses page, whose payload contains mortgage
 * interest it does not own and cannot observe changing. Investments owns everything it shows —
 * nothing outside this module can alter a figure on this page — so the global 30-second `staleTime`
 * is correct and a second fetch on every visit would be waste.
 */

export const INVESTMENTS_KEY = ["investments"] as const;

export function useInvestmentsPage(months = 12): UseQueryResult<InvestmentsPageDto> {
  return useApiQuery<InvestmentsPageDto>(
    [...INVESTMENTS_KEY, "page", months],
    `/investments?months=${months}`,
  );
}

/*
 * `ApiError` IS PART OF THIS SIGNATURE, and leaving it off was hiding something.
 *
 * `useApiQuery` returns `UseQueryResult<T, ApiError>`; annotating this as `UseQueryResult<StockDetailDto>`
 * defaulted the error to `Error` and made `detail.error.status` invisible to callers — so a component could
 * not tell a 404 from a 500 without a cast. 018 needed that distinction (FR-016: a removed stock reached
 * from the activity feed must say it is gone rather than that something went wrong), which is how the
 * omission surfaced.
 */
export function useStockDetail(stockId: string): UseQueryResult<StockDetailDto, ApiError> {
  return useApiQuery<StockDetailDto>(
    [...INVESTMENTS_KEY, "stock", stockId],
    `/investments/stocks/${stockId}`,
  );
}

/**
 * Every write invalidates the WHOLE `investments` key, page and detail alike.
 *
 * A sale recorded on the detail view changes the portfolio total, the closed-positions section and
 * the chart on the page behind it. Invalidating only the view that issued the write is the kind of
 * cache bug that surfaces as a stat card quietly disagreeing with the table under it.
 */
const INVALIDATES = [INVESTMENTS_KEY] as const;

export interface AddStockInput {
  name: string;
  symbol: string;
  exchange?: string | null;
}

export function useAddStock() {
  return useApiMutation<{ id: string }, AddStockInput>(
    (client: ApiClient, input) => client.post<{ id: string }>("/investments/stocks", input),
    { invalidates: INVALIDATES },
  );
}

export function useEditStock() {
  return useApiMutation<void, { stockId: string; name?: string; symbol?: string }>(
    (client, { stockId, ...body }) => client.patch<void>(`/investments/stocks/${stockId}`, body),
    { invalidates: INVALIDATES },
  );
}

export function useRemoveStock() {
  return useApiMutation<void, { stockId: string }>(
    (client, { stockId }) => client.delete<void>(`/investments/stocks/${stockId}`),
    { invalidates: INVALIDATES },
  );
}

export interface RecordTransactionInput {
  stockId: string;
  kind: "buy" | "sell";
  shares: string;
  pricePerShare: string;
  fee?: string;
  date: string;
}

export function useRecordTransaction() {
  return useApiMutation<{ id: string }, RecordTransactionInput>(
    (client, { stockId, ...body }) =>
      client.post<{ id: string }>(`/investments/stocks/${stockId}/transactions`, body),
    { invalidates: INVALIDATES },
  );
}

export function useEditTransaction() {
  return useApiMutation<
    void,
    { transactionId: string; shares?: string; pricePerShare?: string; fee?: string; date?: string }
  >(
    (client, { transactionId, ...body }) =>
      client.patch<void>(`/investments/transactions/${transactionId}`, body),
    { invalidates: INVALIDATES },
  );
}

export function useDeleteTransaction() {
  return useApiMutation<void, { transactionId: string }>(
    (client, { transactionId }) =>
      client.delete<void>(`/investments/transactions/${transactionId}`),
    { invalidates: INVALIDATES },
  );
}

export function useRecordPrice() {
  return useApiMutation<{ id: string }, { stockId: string; price: string; asOfDate: string }>(
    (client, { stockId, ...body }) =>
      client.post<{ id: string }>(`/investments/stocks/${stockId}/prices`, body),
    { invalidates: INVALIDATES },
  );
}

export function useDeletePrice() {
  return useApiMutation<void, { priceId: string }>(
    (client, { priceId }) => client.delete<void>(`/investments/prices/${priceId}`),
    { invalidates: INVALIDATES },
  );
}
